import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Award,
  BookOpen,
  Lightbulb,
  RotateCcw,
} from "lucide-react";
import { GuitarNeck } from "../GuitarNeck";
import { SongLearnCanvasBackdrop } from "../SongLearnCanvasBackdrop";
import { GymIntroModal } from "./GymIntroModal";
import { GymCountdownOverlay, type GymCountdownPhase } from "./GymCountdownOverlay";
import { GymStepPromptCard } from "./GymStepPromptCard";
import {
  GuitarBasicsReferenceSheet,
  GuitarBasicsReferenceTrigger,
} from "./GuitarBasicsReferenceSheet";
import { StudioTunerControl } from "../StudioTunerControl";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { useGymAudioListener } from "../../hooks/useGymAudioListener";
import { GYM_STUDIO } from "../../lib/gym/gymStudioTheme";
import { BEGINNER_PATH_THEME as PATH } from "../../lib/beginnerPath/beginnerPathTheme";
import { BEGINNER_PATH_STUDIO_NECK_CLASS } from "../../lib/beginnerPath/beginnerPathLayout";
import {
  fretPositionsToNeck,
  gymMutedStrings,
} from "../../lib/gym/fretPositionAdapter";
import { targetStringsFromPositions } from "../../lib/gym/gymExerciseTargets";
import { useGymSession } from "./GymExerciseSession";

const COUNTDOWN_TICK_MS = 880;
const COUNTDOWN_GO_MS = 1_000;

type GymExerciseTheme = {
  textMain: string;
  textMuted: string;
  textDim: string;
  bgCard: string;
  border: string;
  bgElevated: string;
  accent: string;
  accentSoft: string;
};

function gymExerciseTheme(pathMode: boolean): GymExerciseTheme {
  if (!pathMode) {
    return {
      textMain: GYM_STUDIO.textMain,
      textMuted: GYM_STUDIO.textMuted,
      textDim: GYM_STUDIO.textDim,
      bgCard: GYM_STUDIO.bgCard,
      border: GYM_STUDIO.border,
      bgElevated: GYM_STUDIO.bgElevated,
      accent: GYM_STUDIO.accent,
      accentSoft: GYM_STUDIO.accentSoft,
    };
  }
  return {
    textMain: PATH.text,
    textMuted: PATH.textMuted,
    textDim: PATH.textDim,
    bgCard: PATH.cardBg,
    border: PATH.cardBorder,
    bgElevated: PATH.cardBg,
    accent: "#99f6e4",
    accentSoft: "rgba(153, 246, 228, 0.12)",
  };
}

export interface GymExerciseStudioProps {
  onBack: () => void;
  pathMode?: boolean;
}

export function GymExerciseStudio({ onBack, pathMode = false }: GymExerciseStudioProps) {
  const C = gymExerciseTheme(pathMode);
  const {
    exercise,
    mode,
    currentStepIdx,
    currentStep,
    totalSteps,
    selectedPositions,
    score,
    attempts,
    showHint,
    showAnswers,
    isCorrect,
    wasCompletedBefore,
    handleStart,
    handleAudioNote,
    stepLocked,
    ripplingStrings,
    usesAudioInput,
    handleQuizOption,
    handleRestart,
    handleReveal,
    setShowHint,
    sessionElapsedSec,
    quizPicked,
  } = useGymSession();

  const [countdown, setCountdown] = useState<GymCountdownPhase | null>(null);
  const [referenceOpen, setReferenceOpen] = useState(false);

  const stepHasQuiz = Boolean(currentStep?.options?.length);
  const exerciseHasFrets = exercise.steps.some((s) => (s.positions?.length ?? 0) > 0);
  const showQuizRow = mode === "playing" && stepHasQuiz;

  const audioEnabled =
    usesAudioInput && mode === "playing" && !stepHasQuiz && !stepLocked && !showAnswers;

  const { isListening, volume } = useGymAudioListener({
    enabled: audioEnabled,
    onStableNote: (note, octave) => handleAudioNote(note, octave),
  });

  const visibleStrings = exercise.stringsNeeded || [1, 2, 3, 4, 5, 6];
  const fretRange = exercise.fretRange || [0, 15];
  const gymMuted = useMemo(() => gymMutedStrings(visibleStrings), [visibleStrings]);

  const neckHighlights = useMemo(() => {
    const out = [];
    if (showAnswers && currentStep?.positions) {
      out.push(...fretPositionsToNeck(currentStep.positions, "root"));
    }
    for (const sp of selectedPositions) {
      if (!out.some((p) => p.string === sp.string && p.fret === sp.fret)) {
        out.push(...fretPositionsToNeck([sp], "active"));
      }
    }
    return out;
  }, [showAnswers, currentStep?.positions, selectedPositions]);

  const highlightedStrings = useMemo(() => {
    if (mode === "playing" && currentStep?.positions && !stepLocked) {
      return targetStringsFromPositions(currentStep.positions);
    }
    if (mode === "intro") {
      const first = exercise.steps.find((s) => s.positions?.length);
      return targetStringsFromPositions(first?.positions);
    }
    return [];
  }, [mode, currentStep?.positions, exercise.steps, stepLocked]);

  const showNeck =
    exerciseHasFrets && (mode === "intro" || mode === "playing") && !showQuizRow;

  const dimNeck = mode === "intro" || countdown !== null;

  const handleBeginCountdown = useCallback(() => {
    setCountdown(3);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "go") {
      const id = window.setTimeout(() => {
        handleStart();
        setCountdown(null);
      }, COUNTDOWN_GO_MS);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      if (typeof countdown === "number") {
        if (countdown <= 1) setCountdown("go");
        else setCountdown(countdown - 1);
      }
    }, COUNTDOWN_TICK_MS);
    return () => window.clearTimeout(id);
  }, [countdown, handleStart]);

  useEffect(() => {
    if (mode === "intro") setCountdown(null);
  }, [mode]);

  const glassPanel = pathMode
    ? "border border-white/[0.08] bg-black/20 backdrop-blur-sm"
    : "border border-white/[0.08] bg-black/30 shadow-[0_8px_32px_rgba(0,0,0,0.35)]";

  return (
    <SongLearnCanvasBackdrop ambient="lite" className="h-full min-h-0 flex-1 w-full">
      <div
        className={`h-full flex w-full flex-col min-h-0 font-sans ${pathMode ? "max-w-[min(100%,1600px)] mx-auto" : "px-3 sm:px-4 pt-3"}`}
        style={{ color: pathMode ? PATH.text : GYM_STUDIO.textMain }}
      >
      <header
        className={cn(
          "shrink-0 flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 sm:px-5",
          glassPanel,
          pathMode && "w-full",
        )}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            type="button"
            onClick={onBack}
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5"
            style={{
              border: `1px solid ${pathMode ? PATH.cardBorder : GYM_STUDIO.border}`,
              color: pathMode ? PATH.textMuted : GYM_STUDIO.textMuted,
            }}
            aria-label={pathMode ? "Volver al paso" : "Volver al gimnasio"}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            {pathMode && (
              <p className="text-[10px] uppercase tracking-[0.14em] text-[#99f6e4]/70 mb-0.5">
                Camino principiante
              </p>
            )}
            <h1 className="text-sm sm:text-base font-serif font-light truncate">{exercise.title}</h1>
            <p className="text-[11px] truncate" style={{ color: pathMode ? PATH.textDim : GYM_STUDIO.textDim }}>
              {exercise.subtitle}
            </p>
          </div>
        </div>

        {mode === "playing" && (
          <div className="flex items-center gap-2 sm:gap-4 shrink-0 text-right text-[10px]">
            <StudioTunerControl compact />
            {pathMode && (
              <GuitarBasicsReferenceTrigger
                onClick={() => setReferenceOpen(true)}
                pathMode
                compact
              />
            )}
            <Stat label="Paso" value={`${currentStepIdx + 1}/${totalSteps}`} theme={C} />
            <Stat
              label="Tiempo"
              value={`${Math.floor(sessionElapsedSec / 60)}:${(sessionElapsedSec % 60).toString().padStart(2, "0")}`}
              theme={C}
            />
            <Stat label="Pts" value={String(score)} accent theme={C} />
            {attempts > 0 && <Stat label="Intentos" value={String(attempts)} warn theme={C} />}
          </div>
        )}
        {mode === "intro" && (
          <div className="flex items-center gap-2 shrink-0">
            <StudioTunerControl compact />
            {pathMode && (
              <GuitarBasicsReferenceTrigger
                onClick={() => setReferenceOpen(true)}
                pathMode
                compact
              />
            )}
          </div>
        )}
      </header>

      <div className={`flex-1 min-h-0 flex flex-col overflow-hidden relative mt-2 mb-2 ${pathMode ? "w-full" : ""}`}>
        {showNeck && (
          <div className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-3 relative rounded-2xl w-full overflow-x-auto overflow-y-hidden">
            <div
              className={cn(
                "relative w-full h-full min-h-[min(48vh,520px)] flex items-center justify-center transition-opacity duration-500 ease-out",
                pathMode ? BEGINNER_PATH_STUDIO_NECK_CLASS : "max-w-[min(96vw,1200px)] mx-auto",
                dimNeck ? "opacity-[0.35]" : "opacity-100",
              )}
            >
              <GuitarNeck
                highlightedPositions={neckHighlights}
                mutedStrings={gymMuted}
                highlightedStrings={highlightedStrings}
                ripplingStrings={ripplingStrings}
                sparseCells
                showNoteNames={showAnswers || mode === "intro"}
                showDegrees={false}
                showFingerBadges={false}
                interactive={false}
                hideFretZero={fretRange[0] > 0}
                practiceFeedback={
                  isCorrect === false ? "fail" : isCorrect === true ? "success" : undefined
                }
              />
              {isCorrect === true && stepLocked && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20 gym-success-pop">
                  <div
                    className="px-8 py-5 rounded-2xl text-center"
                    style={{
                      background: "rgba(34,197,94,0.28)",
                      border: "1px solid rgba(134,239,172,0.45)",
                      boxShadow: "0 0 48px rgba(34,197,94,0.25)",
                    }}
                  >
                    <p className="text-2xl sm:text-3xl font-bold text-emerald-200 mb-1">¡Correcto!</p>
                    {currentStep?.answers?.[0] && (
                      <p className="text-sm text-emerald-100/85">{currentStep.answers[0]}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {mode === "intro" && countdown === null && (
              <GymIntroModal
                exercise={exercise}
                usesAudioInput={usesAudioInput}
                wasCompletedBefore={wasCompletedBefore}
                onBegin={handleBeginCountdown}
                pathMode={pathMode}
                onOpenReference={pathMode ? () => setReferenceOpen(true) : undefined}
              />
            )}
            {countdown !== null && <GymCountdownOverlay phase={countdown} />}
            {mode === "playing" && currentStep && !stepHasQuiz && countdown === null && (
              <GymStepPromptCard
                stepNumber={currentStepIdx + 1}
                instruction={currentStep.instruction}
                showMic={usesAudioInput && !stepLocked}
                isListening={isListening}
                volume={volume}
              />
            )}
          </div>
        )}

        {mode === "completed" && (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center max-w-sm">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.35)" }}
              >
                <Award className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold mb-1">Ejercicio completado</h3>
              <p className="text-sm mb-4" style={{ color: C.textMuted }}>
                {exercise.title}
              </p>
              <div className="flex justify-center gap-4 mb-4">
                <div className="px-4 py-2 rounded-xl" style={{ backgroundColor: C.bgCard, border: `1px solid ${C.border}` }}>
                  <p className="text-[9px] uppercase" style={{ color: C.textDim }}>
                    Puntuación
                  </p>
                  <p className="text-2xl font-bold" style={{ color: C.accent }}>
                    {score}
                  </p>
                </div>
                <div className="px-4 py-2 rounded-xl" style={{ backgroundColor: C.bgCard, border: `1px solid ${C.border}` }}>
                  <p className="text-[9px] uppercase" style={{ color: C.textDim }}>
                    Pasos
                  </p>
                  <p className="text-2xl font-bold text-sky-400">
                    {totalSteps}/{totalSteps}
                  </p>
                </div>
              </div>
              <p className="text-xs italic mb-5" style={{ color: C.textDim }}>
                {exercise.professorNote}
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  onClick={handleRestart}
                  className="border-white/15 bg-white/5"
                  style={{ color: C.textMain }}
                >
                  <RotateCcw className="w-4 h-4" />
                  Repetir
                </Button>
                <Button onClick={onBack} style={{ backgroundColor: C.accent }} className="text-white">
                  Volver al gimnasio
                </Button>
              </div>
            </div>
          </div>
        )}

        {showQuizRow && currentStep?.options && (
          <div
            className="shrink-0 px-4 py-4 border-t"
            style={{ borderColor: C.border, backgroundColor: C.bgCard }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.textDim }}>
              Selecciona todas las opciones correctas
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {currentStep.options.map((option) => {
                const picked = quizPicked.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleQuizOption(option.value)}
                    className={cn(
                      "text-left px-3 py-2.5 rounded-lg border text-sm font-medium transition-all",
                      picked ? "text-white" : "",
                    )}
                    style={
                      picked
                        ? { borderColor: C.accent, backgroundColor: C.accentSoft }
                        : { borderColor: C.border, backgroundColor: C.bgElevated, color: C.textMuted }
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {mode === "playing" && (
        <footer
          className={cn(
            "shrink-0 mb-1 px-4 sm:px-5 py-3 rounded-xl flex flex-wrap items-center justify-between gap-3",
            glassPanel,
            !pathMode && "mx-0",
          )}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowHint((h) => !h)}
              className={cn(
                "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border transition-colors",
                showHint
                  ? "border-amber-400/50 bg-amber-500/20 text-amber-100"
                  : "border-white/20 bg-white/10 text-white hover:bg-white/15",
              )}
            >
              <Lightbulb className="w-3.5 h-3.5" />
              Pista
            </button>
            <button
              type="button"
              onClick={handleReveal}
              disabled={showAnswers}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border border-white/20 bg-white/10 text-white hover:bg-white/15 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              Revelar
            </button>
            <button
              type="button"
              onClick={handleRestart}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border border-white/15 bg-transparent text-white/75 hover:bg-white/10 hover:text-white transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reiniciar
            </button>
          </div>

          {showHint && (
            <p
              className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg order-last sm:order-none"
              style={{ backgroundColor: "rgba(251,191,36,0.12)", color: "#fde68a" }}
            >
              {currentStep?.hint || exercise.professorHint}
            </p>
          )}

          {!showHint && (
            <p className="text-[10px] hidden sm:block max-w-xs text-right" style={{ color: C.textDim }}>
              <BookOpen className="w-3 h-3 inline mr-1 opacity-60" />
              {exercise.professorHint.slice(0, 80)}…
            </p>
          )}
        </footer>
      )}
      </div>
      <style>{`
        .gym-success-pop { animation: gymSuccessPop 0.35s ease-out both; }
        @keyframes gymSuccessPop {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <GuitarBasicsReferenceSheet
        open={referenceOpen}
        onClose={() => setReferenceOpen(false)}
        pathMode={pathMode}
      />
    </SongLearnCanvasBackdrop>
  );
}

function Stat({
  label,
  value,
  accent,
  warn,
  theme,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
  theme: GymExerciseTheme;
}) {
  return (
    <div>
      <p className="uppercase tracking-wider" style={{ color: theme.textDim }}>
        {label}
      </p>
      <p
        className="text-xs font-bold tabular-nums"
        style={{ color: warn ? "#f87171" : accent ? theme.accent : theme.textMain }}
      >
        {value}
      </p>
    </div>
  );
}

export default GymExerciseStudio;
